import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { sendNewPassword } from "../../service/UserService";
import { validatePassword } from "../../utility/validation";
import { encryptPassword } from "../../utility/encryption";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import "./login_lms.css";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [showLoader, setShowLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [message, setMessage] = useState("");

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [passwordChecks, setPasswordChecks] = useState({
    minLength: false,
    hasUpperAndLower: false,
    hasNumber: false,
    hasSpecial: false,
  });
  const [passwordsMatch, setPasswordsMatch] = useState(null);

  const checkPasswordRequirements = (password) => {
    const checks = {
      minLength: password.length >= 8,
      hasUpperAndLower: /[a-z]/.test(password) && /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    setPasswordChecks(checks);
    return Object.values(checks).every((check) => check);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "password") {
      checkPasswordRequirements(value);
      setPasswordsMatch(formData.confirmPassword ? value === formData.confirmPassword : null);

      const retryableErrors = ["SAME_PASSWORD", "DATABASE_ERROR", "DECRYPTION_ERROR", "NETWORK_ERROR", "INTERNAL_ERROR"];
      if (retryableErrors.includes(errorCode)) {
        setMessage("");
        setErrorCode(null);
      }
    }

    if (name === "confirmPassword") {
      setPasswordsMatch(formData.password ? formData.password === value : null);
    }
  };

  const handleFormClear = () => {
    setFormData({ password: "", confirmPassword: "" });
    setPasswordsMatch(null);
    setPasswordChecks({
      minLength: false,
      hasUpperAndLower: false,
      hasNumber: false,
      hasSpecial: false,
    });
  };

  const isFormValid = () =>
    Object.values(passwordChecks).every((c) => c) &&
    passwordsMatch === true &&
    formData.password &&
    formData.confirmPassword;

  const shouldDisableForm = () => {
    const nonRetryableErrors = ["TOKEN_EXPIRED", "TOKEN_INACTIVE", "INVALID_TOKEN", "USER_NOT_FOUND"];
    return nonRetryableErrors.includes(errorCode);
  };

  const handleRequestNewLink = () => navigate("/forget-password");
  const handleGoToLogin = () => navigate("/login");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setErrorCode(null);

    if (!formData.password) { setMessage("New password is required."); return; }
    if (!validatePassword(formData.password)) { setMessage("Password does not meet the requirements."); return; }
    if (!formData.confirmPassword) { setMessage("Please confirm your password."); return; }
    if (formData.password !== formData.confirmPassword) { setMessage("Passwords do not match!"); return; }
    if (!Object.values(passwordChecks).every((check) => check)) { setMessage("Password does not meet all requirements."); return; }

    setShowLoader(true);

    try {
      const encryptedPassword = encryptPassword(formData.password);
      const response = await sendNewPassword({ token, password: encryptedPassword });

      if (response.success) {
        setMessage(response.message);
        setErrorCode(response.code);
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setErrorCode(response.code);
        setMessage(response.message);
        setShowLoader(false);
        if (response.code === "SAME_PASSWORD") handleFormClear();
      }
    } catch (error) {
      setErrorCode("UNKNOWN_ERROR");
      setMessage("An unexpected error occurred. Please try again.");
      setShowLoader(false);
    }
  };

  const renderActionButton = () => {
    if (!errorCode || showLoader) return null;

    switch (errorCode) {
      case "TOKEN_EXPIRED":
      case "TOKEN_INACTIVE":
      case "INVALID_TOKEN":
        return (
          <button onClick={handleRequestNewLink} className="login-button">
            Request New Reset Link
          </button>
        );
      case "SAME_PASSWORD":
        return <p className="signup-link">Please enter a different password above and try again.</p>;
      case "DATABASE_ERROR":
      case "DECRYPTION_ERROR":
      case "INTERNAL_ERROR":
      case "NETWORK_ERROR":
        return <p className="signup-link">The form is still active. Please try submitting again.</p>;
      case "USER_NOT_FOUND":
        return (
          <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "15px" }}>
            <button onClick={handleRequestNewLink} className="login-button">Request New Link</button>
            <button onClick={handleGoToLogin} className="login-button" style={{ backgroundColor: "#6c757d" }}>Go to Login</button>
          </div>
        );
      case "SUCCESS":
        return <p className="signup-link">You will be redirected to login shortly...</p>;
      default:
        return <p className="signup-link">Please try again.</p>;
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h1 className="login-title-lms">Reset Password</h1>
        <p className="login-subtitle-lms">Enter your new password below</p>

        {!token && (
          <>
            <p className="signup-link" style={{ color: "red" }}>
              Invalid or missing reset link. Please check your email and try again.
            </p>
            <button onClick={handleRequestNewLink} className="login-button">
              Request New Reset Link
            </button>
          </>
        )}

        {token && errorCode !== "SUCCESS" && (
          <form onSubmit={handleSubmit}>
            <div className="form-group-login">
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="New Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  disabled={showLoader || shouldDisableForm()}
                  className="form-input"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                >
                  {/* <svg className="eye-icon" viewBox="0 0 24 24">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                  </svg> */}
                   {showPassword ? (
                      <EyeOutlined className="eye-icon" />
                        ) : (
                      <EyeInvisibleOutlined className="eye-icon" />
                        )}
                </button>
              </div>


              {formData.password && (
                <div style={{ textAlign: "left", fontSize: "12px", marginTop: "6px", width: "100%", display: 'flex', flexDirection: 'column' }}>
                  <p style={{ marginBottom: "4px", color: "var(--color-text-muted)" }}>Your password should have:</p>
                  <p style={{ color: passwordChecks.minLength ? "green" : "red", margin: "2px 0" }}>
                    {passwordChecks.minLength ? "✓" : "✗"} At least 8 characters
                  </p>
                  <p style={{ color: passwordChecks.hasUpperAndLower ? "green" : "red", margin: "2px 0" }}>
                    {passwordChecks.hasUpperAndLower ? "✓" : "✗"} Uppercase and lowercase letters
                  </p>
                  <p style={{ color: passwordChecks.hasNumber ? "green" : "red", margin: "2px 0" }}>
                    {passwordChecks.hasNumber ? "✓" : "✗"} At least one number
                  </p>
                  <p style={{ color: passwordChecks.hasSpecial ? "green" : "red", margin: "2px 0" }}>
                    {passwordChecks.hasSpecial ? "✓" : "✗"} At least one special character
                  </p>
                </div>
              )}
            </div>

            <div className="form-group-login">
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm New Password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  disabled={showLoader || !Object.values(passwordChecks).every((c) => c) || shouldDisableForm()}
                  className="form-input"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="password-toggle"
                >
                  {/* <svg className="eye-icon" viewBox="0 0 24 24">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                  </svg> */}
                  {showPassword ? (
                      <EyeOutlined className="eye-icon" />
                        ) : (
                      <EyeInvisibleOutlined className="eye-icon" />
                  )}
                </button>
              </div>

              {formData.confirmPassword && formData.password && (
                <p style={{ color: passwordsMatch ? "green" : "red", fontSize: "12px", textAlign: "left", margin: "4px 0", width: "100%" }}>
                  {passwordsMatch ? "✓ Passwords match" : "✗ Passwords do not match"}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="login-button"
              style={{
                opacity: showLoader || !isFormValid() || shouldDisableForm() ? 0.6 : 1,
                cursor: showLoader || !isFormValid() || shouldDisableForm() ? "not-allowed" : "pointer",
              }}
              disabled={showLoader || !isFormValid() || shouldDisableForm()}
            >
              {showLoader ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}

        {message && (
          <div className="signup-link" style={{
            color: errorCode === "SUCCESS" ? "green" : "red",
            backgroundColor: errorCode === "SUCCESS" ? "#d4edda" : "#f8d7da",
            padding: "10px",
            borderRadius: "5px",
            border: `1px solid ${errorCode === "SUCCESS" ? "#c3e6cb" : "#f5c6cb"}`,
            marginTop: "15px",
          }}>
            {message}
          </div>
        )}

        {renderActionButton()}

        <div className="divider">Or</div>
        <div className="signup-link">
          Remember your password? <a href="/login">Login</a>
        </div>
      </div>
    </div>
  );
}